import { ORGANIZATION_STATUS, type OrganizationStatus } from '@talkium/types';
import { ApiError, api, type RequestOptions } from './client.js';

/**
 * Cliente do plano de operação (PRD §58 a §66, §71, §100, §130).
 *
 * A diferença para o cliente do console não é a rota: é o segundo fator. `/admin/v1/*`
 * exige um código TOTP EM CADA REQUISIÇÃO, não uma vez por sessão. O código vale
 * 30 segundos, com uma janela de um passo para cada lado — na prática, cerca de 90
 * segundos depois de digitado ele deixa de ser aceito.
 *
 * Isso tem uma consequência que a tela não pode esconder: em algum momento, no meio do
 * trabalho, o código expira. Duas decisões saem daí.
 *
 * A primeira é onde o código mora: **só em memória**. `localStorage` sobreviveria ao
 * fechar a aba e guardaria um segundo fator em repouso, num lugar legível por qualquer
 * script da origem — exatamente o que o segundo fator existe para evitar. Recarregar a
 * página pede o código de novo, e isso é o comportamento certo.
 *
 * A segunda é o que acontece quando ele expira. Deixar o erro subir faria o operador
 * perder o formulário que acabou de preencher — o motivo escrito de uma suspensão, por
 * exemplo. Em vez disso, a requisição que bateu no 403 fica suspensa, a tela pede um
 * código novo, e a MESMA requisição é repetida uma vez. Nada do que foi digitado se
 * perde.
 */

/** O código do momento. Nunca persistido — ver o comentário acima. */
let mfaCode: string | null = null;

export function setAdminMfaCode(code: string): void {
  mfaCode = code.replace(/\D/g, '');
}

export function clearAdminMfaCode(): void {
  mfaCode = null;
}

export function hasAdminMfaCode(): boolean {
  return mfaCode !== null;
}

/**
 * Pede um código novo ao operador. Resolve com o código, ou com `null` se ele desistir.
 *
 * Registrado pelo portão de MFA, no mesmo padrão de `setUnauthorizedHandler`: o cliente
 * não sabe desenhar tela, e a tela não sabe quando o código expirou.
 */
type MfaPrompt = (attempt: { invalid: boolean }) => Promise<string | null>;
let requestFreshCode: MfaPrompt = async () => null;
export function setAdminMfaPrompt(prompt: MfaPrompt): void {
  requestFreshCode = prompt;
}

/** A conta ainda não tem segundo fator; o caminho é cadastrar, não redigitar. */
export function isEnrolmentRequired(error: unknown): boolean {
  return reasonOf(error) === 'mfa_enrolment_required';
}

function reasonOf(error: unknown): string | null {
  if (!(error instanceof ApiError) || error.status !== 403) return null;
  const details = error.details as { reason?: unknown } | null | undefined;
  return typeof details?.reason === 'string' ? details.reason : null;
}

export async function adminApi<T>(path: string, options: RequestOptions = {}): Promise<T> {
  // Sem código em mãos a requisição só poderia voltar 403. Pedir antes evita queimar uma
  // chamada e, no limite de taxa `strict`, evita gastar uma tentativa à toa.
  if (mfaCode === null) {
    const first = await requestFreshCode({ invalid: false });
    if (first === null)
      throw new ApiError('FORBIDDEN', 'Código MFA não informado.', 403, null, false);
    setAdminMfaCode(first);
  }

  try {
    return await send<T>(path, options);
  } catch (error) {
    if (reasonOf(error) !== 'mfa_code_required') throw error;

    // O código que tínhamos não serve mais. Descartá-lo antes de pedir outro impede que
    // uma segunda falha reenvie o mesmo valor já recusado.
    clearAdminMfaCode();
    const fresh = await requestFreshCode({ invalid: true });
    if (fresh === null) throw error;

    setAdminMfaCode(fresh);
    // Uma repetição, e só uma: se o código novo também for recusado, insistir viraria um
    // laço contra um endpoint com limite de taxa estrito.
    return send<T>(path, options);
  }
}

/**
 * Confere um código contra o servidor sem acionar o pedido de código novo.
 *
 * É o que o portão usa para destravar: ali o operador já está diante do campo, e o
 * `adminApi` abriria um segundo pedido por cima do primeiro — duas telas pedindo a mesma
 * coisa. Usa a rota mais barata de `/admin/v1` que ainda passa pelo `authenticateAdmin`.
 */
export async function verifyAdminMfaCode(code: string): Promise<void> {
  await api('/admin/v1/flags', { headers: { 'x-talkium-mfa': code.replace(/\D/g, '') } });
}

function send<T>(path: string, options: RequestOptions): Promise<T> {
  return api<T>(path, {
    ...options,
    headers: { ...options.headers, ...(mfaCode ? { 'x-talkium-mfa': mfaCode } : {}) },
  });
}

// --- Formas devolvidas pelo AdminService ------------------------------------

export interface CommandCenter {
  organizations: Record<string, number>;
  instances: {
    total: number;
    connected: number;
    disconnected: number;
    error: number;
    byStatus: Record<string, number>;
  };
  api: {
    counters: Record<string, number>;
    latency: Record<string, { count: number; sum: number; p50: number; p95: number; p99: number }>;
  };
  provider: { circuit: string };
  risk: RiskSignal[];
  business: BusinessBlock;
}

export interface BusinessBlock {
  signupsByDay: Array<{ day: string; count: number }>;
  signupsLast7d: number;
  subscriptionsByStatus: Record<string, number>;
  subscriptionsByPlan: Record<string, number>;
  paymentsByStatus: Record<string, number>;
  succeededCents: number;
  succeededLast30dCents: number;
  pendingCents: number;
  recentOrganizations: Array<{
    id: string;
    name: string;
    slug: string;
    status: string;
    createdAt: string;
  }>;
  recentSubscriptions: Array<{
    organizationId: string;
    planCode: string;
    status: string;
    currentPeriodEnd: string;
    trialEnd: string | null;
  }>;
  recentPayments: Array<{
    id: string;
    organizationId: string;
    amountCents: number;
    currency: string;
    method: string;
    status: string;
    createdAt: string;
  }>;
}

export interface RiskSignal {
  id: string;
  organizationId: string;
  signal: string;
  severity: number;
  action: string;
  createdAt: string;
}

export interface SearchHit {
  kind: string;
  id: string;
  label: string;
  organizationId: string | null;
}

export interface OrganizationRow {
  id: string;
  name: string;
  slug: string;
  status: string;
  createdAt: string;
}

export interface Customer360 {
  organization: OrganizationRow;
  billing: {
    plan: { code: string; name: string; priceCents: number } | null;
    status: string | null;
    mrrCents: number;
    openInvoices: number;
    invoices: Array<{
      number: string;
      status: string;
      totalCents: number;
      dueAt: string | null;
    }>;
  };
  members: Array<{ userId: string; role: string; status: string }>;
  instances: Array<{
    id: string;
    name: string;
    status: string;
    phoneNumber: string | null;
    lastSeenAt: string | null;
    /** O vínculo com o engine: o `instance_key` e de quem é a credencial, nunca ela. */
    engine: {
      provider: string;
      providerInstanceId: string;
      credential: 'instance' | 'platform';
    } | null;
  }>;
  apiKeys: Array<{
    id: string;
    name: string;
    prefix: string;
    environment: string;
    revokedAt: string | null;
    lastUsedAt: string | null;
  }>;
  webhooks: Array<{ id: string; url: string; status: string; failureCount: number }>;
  usage: Record<string, number>;
  recentErrors: Array<{
    requestId: string;
    path: string;
    statusCode: number;
    errorCode: string | null;
    createdAt: string;
  }>;
  tickets: Array<{
    id: string;
    subject: string;
    status: string;
    priority: string;
    createdAt: string;
  }>;
}

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  scope: 'GLOBAL' | 'ORGANIZATION' | 'USER' | 'PERCENTAGE';
  rolloutPct: number;
  targets: string[];
  description?: string | null;
}

/**
 * Os status vêm de `@talkium/types`, não de uma cópia local.
 *
 * O seletor de status de uma organização é o lugar clássico onde uma lista copiada
 * envelhece calada: a plataforma ganha um status novo, a tela de operação continua
 * oferecendo os antigos, e ninguém percebe até precisar usá-lo.
 */
export { ORGANIZATION_STATUS };
export type OrganizationStatusValue = OrganizationStatus;

/**
 * O mínimo que §132 aceita como motivo.
 *
 * Repetido aqui porque o operador precisa saber da regra ANTES de clicar, não depois de
 * um 422. A validação do servidor continua sendo a que vale — esta só evita a viagem.
 */
export const REASON_MIN_LENGTH = 10;

/**
 * Uma linha do catálogo do que o engine emite (§41).
 *
 * `name` é anulável porque existe entrega SEM nome nenhum, e esse caso é distinto de
 * "nome que não conhecemos": um vira evento descartado, o outro vira `system.*`.
 */
export interface EngineEventType {
  name: string | null;
  mapsTo: string | null;
  recognized: boolean;
  shape: unknown;
  occurrences: number;
  firstSeenAt: string;
  lastSeenAt: string;
}

export const adminApiResources = {
  /**
   * Cadastro do segundo fator.
   *
   * Vai por `api` e não por `adminApi` de propósito: são as duas únicas rotas de
   * `/admin/v1` que NÃO passam pelo `authenticateAdmin`, porque exigi-las com código
   * seria pedir o fator que ainda não existe. Roteá-las pelo `adminApi` faria a tela
   * abrir um pedido de código no meio do cadastro do próprio código.
   */
  mfa: {
    setup: () => api<{ secret: string; uri: string }>('/admin/v1/mfa/setup', { method: 'POST' }),
    confirm: (code: string) =>
      api<void>('/admin/v1/mfa/confirm', { method: 'POST', body: { code } }),
  },
  commandCenter: () => adminApi<CommandCenter>('/admin/v1/command-center'),
  engineEvents: () => adminApi<{ data: EngineEventType[] }>('/admin/v1/engine-events'),
  search: (q: string) => adminApi<{ data: SearchHit[] }>('/admin/v1/search', { query: { q } }),
  organizations: (status?: string) =>
    adminApi<{ data: OrganizationRow[] }>('/admin/v1/organizations', {
      query: { ...(status ? { status } : {}) },
    }),
  customer360: (organizationId: string) =>
    adminApi<Customer360>(`/admin/v1/organizations/${organizationId}`),
  setStatus: (organizationId: string, status: OrganizationStatusValue, reason: string) =>
    adminApi<{ id: string; status: string }>(`/admin/v1/organizations/${organizationId}/status`, {
      method: 'POST',
      body: { status, reason },
    }),
  grantCredit: (organizationId: string, amountCents: number, reason: string) =>
    adminApi<{ id: string; amountCents: number }>(
      `/admin/v1/organizations/${organizationId}/credits`,
      { method: 'POST', body: { amountCents, reason } },
    ),
  revokeApiKey: (apiKeyId: string, reason: string) =>
    adminApi<{ id: string; prefix: string }>(`/admin/v1/api-keys/${apiKeyId}/revoke`, {
      method: 'POST',
      body: { reason },
    }),
  revealEngineCredential: (instanceId: string, reason: string) =>
    adminApi<{
      instanceId: string;
      provider: string;
      providerInstanceId: string;
      credential: string;
    }>(`/admin/v1/instances/${instanceId}/engine-credential`, {
      method: 'POST',
      body: { reason },
    }),
  startImpersonation: (organizationId: string, reason: string, minutes: number) =>
    adminApi<{
      id: string;
      organizationId: string;
      expiresAt: string;
      minutes: number;
      /** Devolvido uma vez, na concessão. Não há rota que o releia depois. */
      token: string;
    }>('/admin/v1/impersonation', {
      method: 'POST',
      body: { organizationId, reason, minutes },
    }),
  endImpersonation: (id: string) =>
    adminApi<{ id: string; ended: boolean }>(`/admin/v1/impersonation/${id}`, { method: 'DELETE' }),
  flags: () => adminApi<{ data: FeatureFlag[]; known: string[] }>('/admin/v1/flags'),
  saveFlag: (key: string, body: Omit<FeatureFlag, 'key'>) =>
    adminApi<FeatureFlag>(`/admin/v1/flags/${key}`, { method: 'PUT', body }),
  risk: () => adminApi<{ data: RiskSignal[] }>('/admin/v1/risk'),
};
